import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ActionFeedbackProvider } from '@/contexts/ActionFeedbackContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { Login } from '@/pages/Login';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { Dashboard } from '@/pages/Dashboard';
import { DonasiList } from '@/pages/donasi/DonasiList';
import { BulletinList } from '@/pages/bulletin/BulletinList';
import { BulletinEditor } from '@/pages/bulletin/BulletinEditor';
import { UserList } from '@/pages/users/UserList';
import { ProgramList } from '@/pages/program/ProgramList';
import { ProgramEditor } from '@/pages/program/ProgramEditor';
import { NewsList } from '@/pages/news/NewsList';
import { NewsEditor } from '@/pages/news/NewsEditor';
import { NewsCategoryList } from '@/pages/news/NewsCategoryList';
import { FaqList } from '@/pages/faq/FaqList';
import { FeedbackList } from '@/pages/feedback/FeedbackList';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { KotaList } from '@/pages/kota/KotaList';
import { ProvinsiList } from '@/pages/provinsi/ProvinsiList';
import { PrivacyPolicyList } from '@/pages/privacy-policy/PrivacyPolicyList';
import PrivacyPolicyEditor from '@/pages/privacy-policy/PrivacyPolicyEditor';
import { TermAndConditionsList } from '@/pages/term-and-conditions/TermAndConditionsList';
import TermAndConditionsEditor from '@/pages/term-and-conditions/TermAndConditionsEditor';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasDashboardAccess, isLoading, user, logout } = useAuth();
  const location = useLocation();

  console.log('[ProtectedRoute] Render:', {
    isLoading,
    isAuthenticated,
    hasDashboardAccess,
    user: user?.email,
    roles: user?.roles,
    path: location.pathname,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">Memuat...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Tidakt authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasDashboardAccess) {
    console.warn('[ProtectedRoute] User authenticated without allowed role, redirecting to login', user?.roles);
    void logout();
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasDashboardAccess, isLoading, user } = useAuth();
  const location = useLocation();

  console.log('[PublicRoute] Render:', {
    isLoading,
    isAuthenticated,
    hasDashboardAccess,
    user: user?.email,
    roles: user?.roles,
    path: location.pathname,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">Memuat...</div>
      </div>
    );
  }

  if (isAuthenticated && hasDashboardAccess) {
    const from = (location.state as any)?.from?.pathname || '/';
    console.log('[PublicRoute] Authenticated, redirecting to:', from);
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="donasi" element={<DonasiList />} />
        <Route path="bulletin" element={<BulletinList />} />
        <Route path="bulletin/create" element={<BulletinEditor />} />
        <Route path="bulletin/:id/edit" element={<BulletinEditor />} />
        <Route path="users" element={<UserList />} />
        <Route path="program" element={<ProgramList />} />
        <Route path="program/create" element={<ProgramEditor />} />
        <Route path="program/:id/edit" element={<ProgramEditor />} />
        <Route path="news" element={<NewsList />} />
        <Route path="news/create" element={<NewsEditor />} />
        <Route path="news/:id/edit" element={<NewsEditor />} />
        <Route path="news-categories" element={<NewsCategoryList />} />
        <Route path="artikel" element={<NewsList />} />
        <Route path="faq" element={<FaqList />} />
        <Route path="privacy-policy" element={<PrivacyPolicyList />} />
        <Route path="privacy-policy/create" element={<PrivacyPolicyEditor />} />
        <Route path="privacy-policy/edit" element={<PrivacyPolicyEditor />} />
        <Route path="term-and-conditions" element={<TermAndConditionsList />} />
        <Route path="term-and-conditions/create" element={<TermAndConditionsEditor />} />
        <Route path="term-and-conditions/edit" element={<TermAndConditionsEditor />} />
        <Route path="feedback" element={<FeedbackList />} />
        <Route path="kota" element={<KotaList />} />
        <Route path="provinsi" element={<ProvinsiList />} />
        <Route path="propinsi" element={<Navigate to="/provinsi" replace />} />
        <Route path="pengaturan" element={<SettingsPage />} />
        <Route path="profile" element={<div>Halaman Profil (Segera Hadir)</div>} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ActionFeedbackProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ActionFeedbackProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
